import { useIntl } from '@/intl'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { AlertService, AlertServiceTab } from '@/scopes/prices/components/AlertService'
import { AlternativeFilters } from '@/scopes/prices/components/AlternativeFilters/AlternativeFilters'
import { GridTabs } from '@/scopes/prices/components/GridTabs'
import { Messages } from '@/scopes/prices/components/Messages'
import { PriceBasket } from '@/scopes/prices/components/PriceBasket'
import { PriceBasketLegacy } from '@/scopes/prices/components/PriceBasketLegacy'
import { GridByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/GridByWeeks'
import { LegendByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/LegendByWeeks'
import { MobileNavigationByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/MobileNavigationByWeeks'
import { MonthFilterByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/MonthFilterByWeeks'

import { NavigationByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/NavigationByWeeks'
import { RoomSelector } from '@/scopes/prices/components/RoomSelector/RoomSelector'
import { RoomSelectorLegacy } from '@/scopes/prices/components/RoomSelectorLegacy/RoomSelectorLegacy'
import {
  getFiltersSelector,
  getTransportSelector,
  isPriceTableLoadingStateSelector,
  pricesResponseSelector,
} from '@/scopes/prices/components/selectors'
import { TransportSelector } from '@/scopes/prices/components/TransportSelector'
import { TransportSelectorLegacy } from '@/scopes/prices/components/TransportSelectorLegacy'
import { useScrollToSection } from '@/scopes/prices/domain/prices/hooks'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { space, tokPx, UnavailableContentDialog } from '@sunwebgroup/ui-components'
import * as React from 'react'
import { styled } from 'styled-components'

const PriceTableWrapper = styled.div`
    background: #f3f0ed;
    @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
      padding: ${space('primitives_space_20')};
      border-radius: ${tokPx('primitives_corner_radius_xl')};
  }
`

const Tabs = styled(GridTabs)`
  border-bottom: unset;
  margin-bottom: ${space('primitives_space_20')};
  padding: ${space('primitives_space_4')};
  padding-left: ${space('primitives_space_12')};
    @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
      padding-left: unset;
      margin-right: 0;
  }
`

const TableFooterWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${space('primitives_space_16')} ${space('primitives_space_16')} ${space('primitives_space_48')} ${space('primitives_space_16')};
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    margin-right: 0;
    justify-content: flex-end;
  }
`

const SHOW_NEXT_SEASON_ALERT_TAB = false

export function PriceConfiguratorByWeeks() {
  const intl = useIntl()
  const { isEmpty, error } = usePricesSelector(getFiltersSelector)
  const { alternativeFilters } = usePricesSelector(pricesResponseSelector)
  const isLoading = usePricesSelector(isPriceTableLoadingStateSelector)
  const { loading: fetchingTransport } = usePricesSelector(getTransportSelector)
  const enableUdsRoomSelector = useFeatureFlag('ENABLE_UDS_ROOM_SELECTOR')
  const ENABLE_UDS_PRICE_SUMMARY = useFeatureFlag('ENABLE_UDS_PRICE_SUMMARY')
  useScrollToSection(isLoading, 'RoomSelector')
  useScrollToSection(fetchingTransport, 'FlightSelector')
  const ENABLE_UDS_TRANSPORT_SELECTOR = useFeatureFlag('ENABLE_UDS_TRANSPORT_SELECTOR')

  if (error)
    return null

  if (isEmpty) {
    return (
      <UnavailableContentDialog
        title={intl.formatMessage({ id: 'prices_filters_no-results_title' })}
        message={intl.formatMessage({ id: 'prices_filters_no-results_message' })}
      />
    )
  }
  return (
    <>
      {alternativeFilters
        ? (
            <>
              <Tabs>
                <MonthFilterByWeeks />
                {SHOW_NEXT_SEASON_ALERT_TAB && <AlertServiceTab />}
              </Tabs>
              <AlertService />
              <AlternativeFilters />
            </>
          )
        : (
            <>
              <PriceTableWrapper>
                <Tabs>
                  <MonthFilterByWeeks />
                  {SHOW_NEXT_SEASON_ALERT_TAB && <AlertServiceTab />}
                </Tabs>
                <AlertService />
                <Messages />
                <NavigationByWeeks>
                  <GridByWeeks />
                </NavigationByWeeks>
                <TableFooterWrapper>
                  <LegendByWeeks />
                  <MobileNavigationByWeeks />
                </TableFooterWrapper>
              </PriceTableWrapper>
              {enableUdsRoomSelector ? (<RoomSelector />) : (<RoomSelectorLegacy />)}
              {ENABLE_UDS_TRANSPORT_SELECTOR ? (<TransportSelector />) : (<TransportSelectorLegacy />)}
              { ENABLE_UDS_PRICE_SUMMARY ? <PriceBasket /> : <PriceBasketLegacy />}
            </>
          )}
    </>
  )
}
